/**
 * Form editor for a device's StreamConfig.
 *
 * Two things shape this file:
 *
 * 1. **The console is schema-agnostic.** The API stores whatever JSON it is
 *    given, and the device is the only thing that truly knows the schema. So the
 *    form never rebuilds a config from scratch — it patches keys on the object it
 *    was handed. Anything this build has no field for (alerts, schedule, cloud
 *    settings, keys added by a newer client) rides along untouched.
 *
 * 2. **Reported configs arrive redacted.** The device masks stream keys to
 *    `••••••••` and camera credentials to `//•••:•••@` on every heartbeat. Seeding
 *    an editor from that and pushing it back would set the literal mask as the
 *    stream key and take the device off air. Masked values are detected, shown as
 *    empty with a warning, and block the push until they are re-entered.
 */
import { useState } from 'react'
import { Check, Field, NumberField, SecretField, SelectField, TextField } from './ui'
import CompositionBoard from '@client/renderer/src/CompositionBoard'
import { DEFAULT_CONFIG, type StreamConfig } from '@client/shared/types'

/** Sentinels written by the device's redactConfig() — see client/src/shared/cloud.ts. */
const MASKED_KEY = '••••••••'
const MASKED_CREDS = '//•••:•••@'

const DESTINATION_KINDS = [
  ['youtube', 'YouTube Live'],
  ['facebook', 'Facebook Live'],
  ['custom', 'Custom RTMP']
] as const

const DESTINATION_URLS: Record<string, string> = {
  youtube: 'rtmp://a.rtmp.youtube.com/live2',
  facebook: 'rtmps://live-api-s.facebook.com:443/rtmp',
  custom: ''
}

const ENCODERS = [
  ['libx264', 'libx264 (CPU)'],
  ['h264_qsv', 'h264_qsv (Intel Quick Sync)'],
  ['h264_nvenc', 'h264_nvenc (NVIDIA)'],
  ['h264_amf', 'h264_amf (AMD)'],
  ['h264_videotoolbox', 'h264_videotoolbox (Apple)']
] as const

const PRESETS = [
  ['ultrafast', 'ultrafast'],
  ['superfast', 'superfast'],
  ['veryfast', 'veryfast'],
  ['faster', 'faster'],
  ['fast', 'fast'],
  ['medium', 'medium']
] as const

const STRIP_POSITIONS = [
  ['bottom', 'Bottom'],
  ['top', 'Top'],
  ['left', 'Left'],
  ['right', 'Right']
] as const

const ALIGN_H = [
  ['left', 'Left'],
  ['center', 'Centre'],
  ['right', 'Right']
] as const

const ALIGN_V = [
  ['top', 'Top'],
  ['center', 'Centre'],
  ['bottom', 'Bottom']
] as const

/** 0 = Sunday … 6 = Saturday, matching StreamConfig.scheduleDays. */
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/** Same sections, in the same order, as the desktop client's own tab bar. */
type TabId =
  | 'cameras'
  | 'layout'
  | 'destinations'
  | 'encoding'
  | 'automation'
  | 'reliability'
  | 'advanced'

const TABS: { id: TabId; label: string }[] = [
  { id: 'cameras', label: 'Cameras' },
  { id: 'layout', label: 'Layout' },
  { id: 'destinations', label: 'Destinations' },
  { id: 'encoding', label: 'Encoding' },
  { id: 'automation', label: 'Automation' },
  { id: 'reliability', label: 'Reliability' },
  { id: 'advanced', label: 'Advanced' }
]

const LAYOUTS = [
  ['auto', 'Auto'],
  ['1x1', '1 × 1'],
  ['2x1', '2 × 1'],
  ['2x2', '2 × 2'],
  ['3x2', '3 × 2'],
  ['3x3', '3 × 3'],
  ['4x2', '4 × 2'],
  ['4x3', '4 × 3']
] as const

type Row = Record<string, unknown>
export type Config = Record<string, unknown>

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback)
const num = (v: unknown, fallback: number): number => (typeof v === 'number' ? v : fallback)
const bool = (v: unknown, fallback = false): boolean => (typeof v === 'boolean' ? v : fallback)
const rows = (v: unknown): Row[] => (Array.isArray(v) ? (v as Row[]) : [])

/** Every masked value still sitting in the draft, as human-readable labels. */
export function maskedFields(config: Config): string[] {
  const found: string[] = []
  for (const [i, d] of rows(config.destinations).entries()) {
    if (str(d.streamKey) === MASKED_KEY) {
      found.push(`stream key for "${str(d.name) || `destination ${i + 1}`}"`)
    }
  }
  for (const [i, c] of rows(config.cameras).entries()) {
    if (str(c.url).includes(MASKED_CREDS)) {
      found.push(`credentials in "${str(c.name) || `camera ${i + 1}`}" URL`)
    }
  }
  return found
}

export default function ConfigForm({
  config,
  onChange
}: {
  config: Config
  onChange: (next: Config) => void
}): React.JSX.Element {
  // A masked secret blocks the push, so the tab holding one is where the operator
  // needs to be — open there rather than making them hunt for it.
  const [tab, setTab] = useState<TabId>(() => {
    const masked = maskedFields(config)
    if (masked.some((m) => m.startsWith('stream key'))) return 'destinations'
    if (masked.length > 0) return 'cameras'
    return 'cameras'
  })

  /** Patch one top-level key, leaving every other key exactly as it was. */
  const set = (key: string, value: unknown): void => onChange({ ...config, [key]: value })

  /** Patch one entry of an array-valued key (destinations, cameras). */
  const setRow = (key: string, index: number, patch: Row): void =>
    set(
      key,
      rows(config[key]).map((row, i) => (i === index ? { ...row, ...patch } : row))
    )

  const removeRow = (key: string, index: number): void =>
    set(
      key,
      rows(config[key]).filter((_, i) => i !== index)
    )

  const alerts = (config.alerts as Row | undefined) ?? {}

  /** Patch one alert channel, preserving the other channels and the thresholds. */
  const setChannel = (channel: string, patch: Row): void =>
    set('alerts', {
      ...alerts,
      [channel]: { ...((alerts[channel] as Row | undefined) ?? {}), ...patch }
    })

  const scheduleDays = Array.isArray(config.scheduleDays) ? (config.scheduleDays as number[]) : []

  const toggleDay = (day: number): void =>
    set(
      'scheduleDays',
      scheduleDays.includes(day)
        ? scheduleDays.filter((d) => d !== day)
        : [...scheduleDays, day].sort((a, b) => a - b)
    )

  const destinations = rows(config.destinations)
  const cameras = rows(config.cameras)
  const mosaic = cameras.filter((c) => bool(c.enabled, true)).length > 1

  /**
   * Which tabs are hiding something the operator has to deal with. Only conditions
   * that actually block or break the push earn a dot — a dot that means "look here
   * eventually" trains people to ignore all of them.
   */
  const alarms: Partial<Record<TabId, boolean>> = {
    destinations:
      destinations.some((d) => str(d.streamKey) === MASKED_KEY) ||
      destinations.filter((d) => bool(d.enabled, true)).length === 0,
    cameras:
      cameras.some((c) => str(c.url).includes(MASKED_CREDS)) ||
      cameras.filter((c) => bool(c.enabled, true)).length === 0
  }

  const addDestination = (): void =>
    set('destinations', [
      ...destinations,
      {
        id: `dest-${Date.now()}`,
        name: 'YouTube Live',
        kind: 'youtube',
        url: DESTINATION_URLS.youtube,
        streamKey: '',
        enabled: true,
        useBackup: false
      }
    ])

  const addCamera = (): void =>
    set('cameras', [
      ...cameras,
      {
        id: `cam-${Date.now()}`,
        name: `Camera ${cameras.length + 1}`,
        url: '',
        enabled: true,
        kind: 'rtsp',
        device: '',
        audioDevice: '',
        deviceSize: '',
        deviceFps: 0
      }
    ])

  return (
    <div className="stack">
      {/* Mirrors the client's own section bar: counts on the list-shaped tabs, and
          an alarm dot wherever something still blocks the push. */}
      <nav className="subtabs">
        {TABS.map((t) => (
          <button
            type="button"
            key={t.id}
            className={`tab ${tab === t.id ? 'on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'cameras' && <span className="count">{cameras.length}</span>}
            {t.id === 'destinations' && <span className="count">{destinations.length}</span>}
            {alarms[t.id] && <span className="alarm" />}
          </button>
        ))}
      </nav>

      {/* ---- destinations ---- */}
      {tab === 'destinations' && (
      <div className="group">
        <div className="group-head">
          <h3>Destinations</h3>
          <div className="spacer" />
          <button type="button" className="small" onClick={addDestination}>
            Add destination
          </button>
        </div>

        {destinations.length === 0 && (
          <p className="hint">No destinations — the device has nowhere to stream.</p>
        )}

        {destinations.map((d, i) => {
          const kind = str(d.kind, 'youtube')
          const masked = str(d.streamKey) === MASKED_KEY
          return (
            <div className="sub-card" key={str(d.id) || i}>
              <div className="sub-head">
                <Check
                  checked={bool(d.enabled, true)}
                  onChange={(v) => setRow('destinations', i, { enabled: v })}
                >
                  {str(d.name) || `Destination ${i + 1}`}
                </Check>
                <div className="spacer" />
                <button
                  type="button"
                  className="small danger"
                  onClick={() => removeRow('destinations', i)}
                >
                  Remove
                </button>
              </div>

              <div className="row">
                <TextField
                  label="Name"
                  value={str(d.name)}
                  onChange={(v) => setRow('destinations', i, { name: v })}
                />
                <SelectField
                  label="Service"
                  value={kind}
                  options={DESTINATION_KINDS}
                  onChange={(v) =>
                    // Switching service swaps in that service's ingest URL, but only
                    // when the current one is still a known preset — a hand-typed
                    // URL is the operator's, not ours to overwrite.
                    setRow('destinations', i, {
                      kind: v,
                      ...(Object.values(DESTINATION_URLS).includes(str(d.url))
                        ? { url: DESTINATION_URLS[v] }
                        : {}),
                      ...(v === 'youtube' ? {} : { useBackup: false })
                    })
                  }
                />
              </div>

              <TextField
                label="Ingest URL"
                value={str(d.url)}
                placeholder="rtmp://…"
                onChange={(v) => setRow('destinations', i, { url: v })}
              />

              <SecretField
                label="Stream key"
                value={masked ? '' : str(d.streamKey)}
                placeholder={masked ? 'masked by the device — re-enter to push' : ''}
                hint={
                  masked ? (
                    <span className="hint warn">
                      The device reports this key masked, so the console cannot read it back.
                      Re-enter it, or remove this destination, before pushing.
                    </span>
                  ) : undefined
                }
                onChange={(v) => setRow('destinations', i, { streamKey: v })}
              />

              {kind === 'youtube' && (
                <Check
                  checked={bool(d.useBackup)}
                  onChange={(v) => setRow('destinations', i, { useBackup: v })}
                >
                  Also push to YouTube&apos;s backup ingest (redundancy)
                </Check>
              )}
            </div>
          )
        })}
      </div>

      )}

      {/* ---- cameras ---- */}
      {tab === 'cameras' && (
      <div className="group">
        <div className="group-head">
          <h3>Cameras</h3>
          <div className="spacer" />
          <button type="button" className="small" onClick={addCamera}>
            Add camera
          </button>
        </div>

        {cameras.map((c, i) => {
          const kind = str(c.kind, 'rtsp')
          const maskedUrl = str(c.url).includes(MASKED_CREDS)
          return (
            <div className="sub-card" key={str(c.id) || i}>
              <div className="sub-head">
                <Check
                  checked={bool(c.enabled, true)}
                  onChange={(v) => setRow('cameras', i, { enabled: v })}
                >
                  {str(c.name) || `Camera ${i + 1}`}
                </Check>
                <div className="spacer" />
                <button
                  type="button"
                  className="small danger"
                  onClick={() => removeRow('cameras', i)}
                >
                  Remove
                </button>
              </div>

              <div className="row">
                <TextField
                  label="Name"
                  value={str(c.name)}
                  onChange={(v) => setRow('cameras', i, { name: v })}
                />
                <SelectField
                  label="Source"
                  value={kind}
                  options={
                    [
                      ['rtsp', 'RTSP / network'],
                      ['device', 'Capture device'],
                      ['file', 'Video file (MP4)'],
                      ['url', 'Online stream (URL)'],
                      ['picture', 'Picture (PNG/JPG)']
                    ] as const
                  }
                  onChange={(v) => setRow('cameras', i, { kind: v })}
                />
              </div>

              {kind === 'picture' ? (
                <TextField
                  label="Picture file"
                  value={str(c.url)}
                  placeholder="C:\images\map.png"
                  hint="A path on the device. Shown as an endless still camera."
                  onChange={(v) => setRow('cameras', i, { url: v })}
                />
              ) : kind === 'url' ? (
                <TextField
                  label="Stream URL"
                  value={maskedUrl ? '' : str(c.url)}
                  placeholder={
                    maskedUrl ? 'retype the full URL, with credentials' : 'https://host/live.m3u8'
                  }
                  hint={
                    maskedUrl ? (
                      <span className="hint warn">
                        The device masked the credentials in this URL. Retype it in full before
                        pushing.
                      </span>
                    ) : (
                      'HLS, DASH, MP4 over HTTP, SRT or UDP. Not a YouTube or Facebook page link — those serve a web page, not video.'
                    )
                  }
                  onChange={(v) => setRow('cameras', i, { url: v })}
                />
              ) : kind === 'file' ? (
                <TextField
                  label="Video file"
                  // Shares the url field with an RTSP camera, but never carries
                  // credentials, so none of the masking below applies. The path is
                  // the device's own, not this machine's — there is nothing to
                  // browse for from here.
                  value={str(c.url)}
                  placeholder="C:\videos\clip.mp4"
                  hint="A path on the device. Played on a loop, paced to real time."
                  onChange={(v) => setRow('cameras', i, { url: v })}
                />
              ) : kind === 'device' ? (
                <>
                  <TextField
                    label="Video device"
                    value={str(c.device)}
                    hint="The device name as the host's capture backend reports it."
                    onChange={(v) => setRow('cameras', i, { device: v })}
                  />
                  <TextField
                    label="Audio device (optional)"
                    value={str(c.audioDevice)}
                    onChange={(v) => setRow('cameras', i, { audioDevice: v })}
                  />
                </>
              ) : (
                <TextField
                  label="RTSP URL"
                  // Left blank on purpose when masked: a pre-filled mask is one
                  // stray "Push" away from becoming the device's real URL. The
                  // original is shown below as read-only context instead, so the
                  // host and path are still there to copy from.
                  value={maskedUrl ? '' : str(c.url)}
                  placeholder={maskedUrl ? 'retype the full URL, with credentials' : 'rtsp://…'}
                  hint={
                    maskedUrl ? (
                      <>
                        <span className="hint warn">
                          The device masked the username and password. Retype the URL in full
                          before pushing — only the credentials are missing.
                        </span>
                        <code className="mono reported-url">{str(c.url)}</code>
                      </>
                    ) : undefined
                  }
                  onChange={(v) => setRow('cameras', i, { url: v })}
                />
              )}
            </div>
          )
        })}
      </div>

      )}

      {/* ---- layout ---- */}
      {tab === 'layout' && (
      <div className="group">
        <div className="group-head">
          <h3>Layout</h3>
        </div>

        <div className="row">
          <SelectField
            label="Mode"
            value={str(config.mode, 'grid')}
            options={
              [
                ['grid', 'Grid — equal tiles'],
                ['spotlight', 'Spotlight — one large + strip']
              ] as const
            }
            onChange={(v) => set('mode', v)}
          />
          <SelectField
            label="Grid"
            value={str(config.layout, 'auto')}
            options={LAYOUTS}
            onChange={(v) => set('layout', v)}
          />
        </div>

        <div className="row three">
          <NumberField
            label="Width"
            min={160}
            value={num(config.outputWidth, 1920)}
            onChange={(v) => set('outputWidth', v)}
          />
          <NumberField
            label="Height"
            min={120}
            value={num(config.outputHeight, 1080)}
            onChange={(v) => set('outputHeight', v)}
          />
          <SelectField
            label="Tile fit"
            value={str(config.tileFit, 'contain')}
            options={
              [
                ['contain', 'Contain — letterbox'],
                ['cover', 'Cover — crop to fill']
              ] as const
            }
            onChange={(v) => set('tileFit', v)}
          />
        </div>

        {str(config.tileFit, 'contain') === 'cover' && (
          <SelectField
            label="Crop from"
            value={str(config.tileFitAnchor, 'center')}
            options={
              [
                ['center', 'Both edges (centre)'],
                ['top', 'Bottom only — keep the top'],
                ['bottom', 'Top only — keep the bottom']
              ] as const
            }
            hint="Cameras burn their date and time into one edge; keep that edge and crop the other."
            onChange={(v) => set('tileFitAnchor', v)}
          />
        )}

        {str(config.mode, 'grid') === 'grid' ? (
          <div className="row">
            <SelectField
              label="Horizontal alignment"
              value={str(config.gridAlignH, 'center')}
              options={ALIGN_H}
              hint="Where the tile block — and a partial last row — sits on the canvas."
              onChange={(v) => set('gridAlignH', v)}
            />
            <SelectField
              label="Vertical alignment"
              value={str(config.gridAlignV, 'center')}
              options={ALIGN_V}
              onChange={(v) => set('gridAlignV', v)}
            />
          </div>
        ) : (
          <>
            <div className="row">
              <Field label="Main camera" hint="Fills the background; the rest become thumbnails.">
                <select
                  value={String(num(config.mainCameraIndex, 0))}
                  onChange={(e) => set('mainCameraIndex', Number(e.target.value))}
                >
                  {cameras
                    .filter((c) => bool(c.enabled, true))
                    .map((c, i) => (
                      <option key={i} value={i}>
                        {str(c.name) || `Camera ${i + 1}`}
                      </option>
                    ))}
                </select>
              </Field>
              <SelectField
                label="Thumbnail strip"
                value={str(config.stripPosition, 'bottom')}
                options={STRIP_POSITIONS}
                onChange={(v) => set('stripPosition', v)}
              />
            </div>

            <Field
              label={`Thumbnail size — ${Math.round(num(config.thumbScale, 0.18) * 100)}% of the canvas`}
            >
              <input
                type="range"
                min={5}
                max={40}
                value={Math.round(num(config.thumbScale, 0.18) * 100)}
                onChange={(e) => set('thumbScale', Number(e.target.value) / 100)}
              />
            </Field>

            <div className="row three">
              <NumberField
                label="Gap (px)"
                value={num(config.thumbGap, 10)}
                onChange={(v) => set('thumbGap', v)}
              />
              <NumberField
                label="Margin (px)"
                value={num(config.thumbMargin, 16)}
                onChange={(v) => set('thumbMargin', v)}
              />
              <NumberField
                label="Border (px)"
                value={num(config.thumbBorder, 2)}
                onChange={(v) => set('thumbBorder', v)}
              />
            </div>
          </>
        )}

        <Check checked={bool(config.showLabels, true)} onChange={(v) => set('showLabels', v)}>
          Draw each camera&apos;s name on its tile
        </Check>

        {/*
          The device's own composition board, driven by this draft. Defaults fill
          the gaps so a config written by an older client — or a hand-edited one
          missing a key — still renders instead of throwing; only keys the
          operator actually changes are written back.
        */}
        {mosaic && (
          <CompositionBoard
            config={{ ...DEFAULT_CONFIG, ...(config as Partial<StreamConfig>) } as StreamConfig}
            locked={false}
            onPatch={(changes) => onChange({ ...config, ...changes })}
          />
        )}

        {!mosaic && (
          <p className="hint">
            Layout applies once two or more cameras are enabled — a single camera streams
            straight through.
          </p>
        )}
      </div>

      )}

      {/* ---- video ---- */}
      {tab === 'encoding' && (
      <div className="group">
        <div className="group-head">
          <h3>Video</h3>
        </div>

        <div className="row">
          <SelectField
            label="Mode"
            value={str(config.videoMode, 'encode')}
            options={
              [
                ['encode', 'Re-encode'],
                ['copy', 'Copy stream (single camera only)']
              ] as const
            }
            hint={
              mosaic && str(config.videoMode) === 'copy'
                ? 'A mosaic is composited, so the device re-encodes regardless.'
                : undefined
            }
            onChange={(v) => set('videoMode', v)}
          />
          <SelectField
            label="Encoder"
            value={str(config.encoder, 'libx264')}
            options={ENCODERS}
            hint="Must exist on the target machine — hardware encoders are platform-specific."
            onChange={(v) => set('encoder', v)}
          />
        </div>

        <div className="row three">
          <NumberField
            label="Bitrate (kb/s)"
            min={100}
            value={num(config.videoBitrateKbps, 5000)}
            onChange={(v) => set('videoBitrateKbps', v)}
          />
          <NumberField
            label="Buffer (kb/s)"
            min={100}
            value={num(config.bufsizeKbps, 10000)}
            onChange={(v) => set('bufsizeKbps', v)}
          />
          <NumberField
            label="FPS"
            min={1}
            value={num(config.fps, 30)}
            onChange={(v) => set('fps', v)}
          />
        </div>

        <div className="row">
          <NumberField
            label="Keyframe interval (frames)"
            min={1}
            value={num(config.gop, 60)}
            hint="YouTube wants a keyframe at least every 2s — so fps × 2 or less."
            onChange={(v) => set('gop', v)}
          />
          <SelectField
            label="Preset (libx264 only)"
            value={str(config.preset, 'veryfast')}
            options={PRESETS}
            onChange={(v) => set('preset', v)}
          />
        </div>
      </div>

      )}

      {/* ---- audio ---- */}
      {tab === 'encoding' && (
      <div className="group">
        <div className="group-head">
          <h3>Audio</h3>
        </div>

        <div className="row three">
          <SelectField
            label="Source"
            value={str(config.audioMode, 'silent')}
            options={
              [
                ['silent', 'Silent track'],
                ['camera', 'From a camera']
              ] as const
            }
            onChange={(v) => set('audioMode', v)}
          />
          <NumberField
            label="Bitrate (kb/s)"
            min={32}
            value={num(config.audioBitrateKbps, 128)}
            onChange={(v) => set('audioBitrateKbps', v)}
          />
          <NumberField
            label="Sample rate"
            min={8000}
            value={num(config.sampleRate, 44100)}
            onChange={(v) => set('sampleRate', v)}
          />
        </div>

        {str(config.audioMode) === 'camera' && (
          <Field
            label="Camera"
            hint="Index into the enabled cameras. A camera with no audio track fails the run."
          >
            <select
              value={String(num(config.audioCameraIndex, 0))}
              onChange={(e) => set('audioCameraIndex', Number(e.target.value))}
            >
              {cameras
                .filter((c) => bool(c.enabled, true))
                .map((c, i) => (
                  <option key={i} value={i}>
                    {str(c.name) || `Camera ${i + 1}`}
                  </option>
                ))}
            </select>
          </Field>
        )}
      </div>

      )}

      {/* ---- reliability ---- */}
      {tab === 'reliability' && (
      <div className="group">
        <div className="group-head">
          <h3>Reliability</h3>
        </div>

        <Check checked={bool(config.autoRestart, true)} onChange={(v) => set('autoRestart', v)}>
          Restart automatically when the stream drops
        </Check>
        <Check
          checked={bool(config.skipDeadCameras, true)}
          onChange={(v) => set('skipDeadCameras', v)}
        >
          Probe first and stream without cameras that do not answer
        </Check>
        <Check
          checked={bool(config.placeholderTiles, true)}
          onChange={(v) => set('placeholderTiles', v)}
        >
          Keep a dead camera&apos;s tile as an OFFLINE placeholder
        </Check>
        <Check
          checked={bool(config.previewEnabled, true)}
          onChange={(v) => set('previewEnabled', v)}
        >
          Write a preview image of the composed output
        </Check>

        <div className="row three">
          <NumberField
            label="Restart delay (s)"
            min={1}
            value={num(config.restartDelaySec, 5)}
            onChange={(v) => set('restartDelaySec', v)}
          />
          <NumberField
            label="Max delay (s)"
            min={1}
            value={num(config.maxRestartDelaySec, 60)}
            onChange={(v) => set('maxRestartDelaySec', v)}
          />
          <NumberField
            label="Stall timeout (s)"
            min={1}
            value={num(config.stallTimeoutSec, 20)}
            onChange={(v) => set('stallTimeoutSec', v)}
          />
        </div>

        <NumberField
          label="Re-probe skipped cameras every (minutes)"
          min={0}
          value={num(config.rescanMinutes, 2)}
          hint="0 turns the rescan off — a skipped camera then stays out until the next restart."
          onChange={(v) => set('rescanMinutes', v)}
        />
      </div>

      )}

      {/* ---- schedule ---- */}
      {tab === 'automation' && (
      <div className="group">
        <div className="group-head">
          <h3>Schedule</h3>
        </div>

        <Check
          checked={bool(config.scheduleEnabled)}
          onChange={(v) => set('scheduleEnabled', v)}
        >
          Start and stop automatically inside a daily window
        </Check>

        {bool(config.scheduleEnabled) && (
          <>
            <div className="row">
              <Field label="Start">
                <input
                  type="time"
                  value={str(config.scheduleStart, '08:00')}
                  onChange={(e) => set('scheduleStart', e.target.value)}
                />
              </Field>
              <Field label="End">
                <input
                  type="time"
                  value={str(config.scheduleEnd, '18:00')}
                  onChange={(e) => set('scheduleEnd', e.target.value)}
                />
              </Field>
            </div>

            <Field
              label="Days"
              hint="Keyed on the day the window opens, so an overnight window belongs to its start day."
            >
              <div className="day-picker">
                {DAYS.map((name, day) => (
                  <button
                    type="button"
                    key={day}
                    className={scheduleDays.includes(day) ? 'on' : ''}
                    onClick={() => toggleDay(day)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </Field>

            <p className="hint">
              Times are the device&apos;s own local clock, which may not be yours.
            </p>
          </>
        )}
      </div>

      )}

      {/* ---- alerts ---- */}
      {tab === 'automation' && (
      <div className="group">
        <div className="group-head">
          <h3>Alerts</h3>
        </div>

        <Check
          checked={bool((alerts.telegram as Row | undefined)?.enabled)}
          onChange={(v) => setChannel('telegram', { enabled: v })}
        >
          Telegram
        </Check>
        {bool((alerts.telegram as Row | undefined)?.enabled) && (
          <div className="row">
            <SecretField
              label="Bot token"
              value={str((alerts.telegram as Row | undefined)?.botToken)}
              onChange={(v) => setChannel('telegram', { botToken: v })}
            />
            <TextField
              label="Chat ID"
              value={str((alerts.telegram as Row | undefined)?.chatId)}
              onChange={(v) => setChannel('telegram', { chatId: v })}
            />
          </div>
        )}

        <Check
          checked={bool((alerts.discord as Row | undefined)?.enabled)}
          onChange={(v) => setChannel('discord', { enabled: v })}
        >
          Discord
        </Check>
        {bool((alerts.discord as Row | undefined)?.enabled) && (
          <SecretField
            label="Webhook URL"
            value={str((alerts.discord as Row | undefined)?.webhookUrl)}
            onChange={(v) => setChannel('discord', { webhookUrl: v })}
          />
        )}

        <Check
          checked={bool((alerts.webhook as Row | undefined)?.enabled)}
          onChange={(v) => setChannel('webhook', { enabled: v })}
        >
          Generic webhook
        </Check>
        {bool((alerts.webhook as Row | undefined)?.enabled) && (
          <SecretField
            label="URL"
            value={str((alerts.webhook as Row | undefined)?.url)}
            onChange={(v) => setChannel('webhook', { url: v })}
          />
        )}

        <div className="row">
          <NumberField
            label="Down threshold (s)"
            min={0}
            value={num(alerts.downThresholdSec, 30)}
            hint="How long it must stay down before alerting — rides through fast restarts."
            onChange={(v) => set('alerts', { ...alerts, downThresholdSec: v })}
          />
          <NumberField
            label="Cooldown (s)"
            min={0}
            value={num(alerts.cooldownSec, 300)}
            hint="Minimum gap between two alerts of the same kind."
            onChange={(v) => set('alerts', { ...alerts, cooldownSec: v })}
          />
        </div>

        <Check
          checked={bool(alerts.notifyManualStartStop)}
          onChange={(v) => set('alerts', { ...alerts, notifyManualStartStop: v })}
        >
          Also announce start and stop the operator asked for, not just outages
        </Check>
      </div>

      )}

      {/* ---- advanced ---- */}
      {tab === 'advanced' && (
      <div className="group">
        <div className="group-head">
          <h3>Advanced</h3>
        </div>

        <div className="row">
          <SelectField
            label="RTSP transport"
            value={str(config.rtspTransport, 'tcp')}
            options={
              [
                ['tcp', 'TCP — reliable'],
                ['udp', 'UDP — lower latency']
              ] as const
            }
            onChange={(v) => set('rtspTransport', v)}
          />
          <TextField
            label="ffmpeg path"
            value={str(config.ffmpegPath, 'ffmpeg')}
            placeholder="ffmpeg"
            hint="A bare name is resolved on the device's PATH."
            onChange={(v) => set('ffmpegPath', v)}
          />
        </div>

        <Check checked={bool(config.autoStart)} onChange={(v) => set('autoStart', v)}>
          Start streaming as soon as the app launches
        </Check>
        <Check checked={bool(config.launchAtLogin)} onChange={(v) => set('launchAtLogin', v)}>
          Launch the app when the user logs in
        </Check>
        <Check checked={bool(config.closeToTray, true)} onChange={(v) => set('closeToTray', v)}>
          Closing the window hides to the tray instead of quitting
        </Check>
        <Check checked={bool(config.startMinimized)} onChange={(v) => set('startMinimized', v)}>
          Start hidden in the tray
        </Check>
      </div>
      )}
    </div>
  )
}
